import { Component, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, NavigationStart, ActivatedRouteSnapshot } from '@angular/router';
import { Subscription } from 'rxjs';
import { Enums } from '../app/_shared/enums/enums';
import * as Models from './_shared/models/models-index';
import * as Services from './_shared/services/services-index';
import { ToasterConfig } from '@dkachko/angular2-toaster';
import { forkJoin } from 'rxjs';
import { environment } from '../environments/environment';
import { map, filter, tap } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { AppState } from './_store/app-state.model';
import { selectLoggedInUser } from './_store/app.selectors';
import { AppActions } from './_store/action-types';
import { FilterActions } from './_shared/filter/store';
import { AppSelectors } from './_store/selector-types';
import { Title } from '@angular/platform-browser';
import { RouterLinkBackService } from './_shared/directives/router-link-back.service';

// Monaco is available globally once loaded by the editor module.
declare const monaco: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  toasterConfig: ToasterConfig = undefined;
  private routerSub: Subscription;
  private isUnsubscribe = true;
  private publicRoutes: string[] = ['/login', '/ssologin', '/ssologinV2', '/logout'];
  private baseUrl: string = environment.hostName;
  private user$ = this.store$.pipe(select(selectLoggedInUser));

  favIcon: HTMLLinkElement = document.querySelector('#appIcon');
  loadConfigCount = 0;
  loadConfigLimit = 10; // this is the limit of how many times we will try to load the config before we start logging errors

  constructor(
    private store$: Store<AppState>,
    private appInsights: Services.AppInsightsService,
    private authService: Services.AuthenticationService,
    private customMaterialIconsService: Services.CustomMaterialIconsService,
    private configService: Services.ConfigurationService,
    private router: Router,
    private titleService: Title,
    private filterService: Services.FilterService,
    private cache: Services.LocalCacheService,
    private orgService: Services.OrgLookupService,
    private translationService: Services.TranslationService,
    private _: RouterLinkBackService, // Inject in root component so navigation tracking is initialized early
    private routingConfigService: Services.RoutingConfigService,
  ) {

    this.appInsights.setHostDomain(this.configService.hostInformation.hostName);
    this.toasterConfig = this.configService.toasterConfig;
  }

  verifyLoggedIn$ = this.user$.pipe(
    map(user => {
      if (user) {
        const exp = user.expireDate;
        const oneDay = (23 * 60 * 60 + 1) * 1000;
        const diff = exp.getTime() - (new Date()).getTime();
        if (diff > oneDay) {
          return true;
        }
      }
      return false;
    })
  );

  ngOnInit() {
    this.store$.dispatch(AppActions.loadConfig()); // need this to fetch FrontEnd resourceStrings
    this.customMaterialIconsService.registerIcons();
    this.titleService.setTitle(environment.appName);
    this.favIcon.href = environment.favIconPath;


    const isSsoV2 = location.pathname.includes('ssoLoginV2') ? location.search.lastIndexOf('?authData', 0) === 0 : false;
    const isSso = location.search.lastIndexOf('?authData', 0) === 0;

    if (isSsoV2) {
      this.router.navigateByUrl('/ssoLoginV2' + location.search);
    } else if (isSso) {
      this.router.navigateByUrl('/ssoLogin' + location.search);
    }

    // Setup redirect route when navigating to /
    this.store$.select(AppSelectors.selectLoggedInUserAndConfig).pipe(
      tap(([userInfo, config]) => {
        console.log('userInfo', userInfo);
          if (!!userInfo && (!config || !config.resourceStrings)) {
            this.store$.dispatch(AppActions.loadConfig());
          }
      }),
      filter(([user, _]) => !!user && user.isAuthenticated)
    ).subscribe(([user, _]) => {
      const defaultRoute = this.routingConfigService.defaultRoute;
      console.log('RoutingConfigService defaultRoute', defaultRoute);
      if (!user) {
        console.warn('User is not authenticated, redirecting to login');
        this.router.navigateByUrl('/login');
      }
    });

    this.routerSub = this.router.events.subscribe((evt) => {
      if ((evt instanceof NavigationStart)) {
        // We don't want to call on the filterService when we're not authenticated because we don't have a hydrated authData object and the app pukes
        if (!this.publicRoutes.includes(evt.url.toLowerCase())) {
        }
      }
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
      this.trackPageView(evt);
    });

    this.setupMonacoEditor();
  }

  setupMonacoEditor() {
    this.waitForMonaco().then(() => {
      monaco.editor.defineTheme('appTheme', {
        base: 'vs-dark',
        inherit: true,
        rules: [
          { token: 'comment', foreground: 'ffa500', fontStyle: 'italic underline' },
          { token: 'comment.js', foreground: '008800', fontStyle: 'bold' },
          { token: 'comment.css', foreground: '0000ff' }
        ],
        colors: {}
      });

      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemas: []
      });
    });
  }

  private waitForMonaco(): Promise<void> {
    return new Promise((resolve) => {
      const check = () => {
        if (typeof monaco !== 'undefined' && monaco.editor?.defineTheme) {
          resolve();
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }

  ngOnDestroy() {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  private trackPageView(evt: NavigationEnd): void {
    const root = this.router.routerState.snapshot.root;
    const deepest = this.getDeepestRoute(root);
    const routeData = this.mergeRouteData(root);
    const routePath = this.getRoutePath(root);
    this.appInsights.trackRoutePageView({ ...routeData, ...deepest.data }, evt.urlAfterRedirects || evt.url, routePath);
  }

  private getDeepestRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let current = route;
    while (current.firstChild) {
      current = current.firstChild;
    }
    return current;
  }

  private mergeRouteData(route: ActivatedRouteSnapshot): any {
    let current: ActivatedRouteSnapshot = route;
    let data = { ...(current.data || {}) };
    while (current.firstChild) {
      current = current.firstChild;
      data = { ...data, ...(current.data || {}) };
    }
    return data;
  }

  private getRoutePath(route: ActivatedRouteSnapshot): string {
    const parts: string[] = [];
    let current: ActivatedRouteSnapshot = route;
    while (current) {
      const path = current.routeConfig?.path;
      if (path) {
        parts.push(path);
      }
      current = current.firstChild;
    }
    return parts.join('/');
  }
  
}
